import LoadFailure from '../components/Brand/LoadFailure';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Layout } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellOnIcon, HeartIcon, RepostIcon, FollowIcon, PlusIcon } from '../components/Brand/Icon';
import UserImage from '../components/UserImage';
import BrandLoader from '../components/Brand/BrandLoader';
import WiwHeaderTitle from '../components/WiwHeaderTitle';
import ButtonPanelReaction from '../components/Buttons/ButtonPanelReaction';
import usePageLayout from '../hooks/usePageLayout';
import { getUser, getLocalStorage, createName } from '../utils/func';
import config from '../utils/config';
import url from '../utils/url';
import { notificationRequest, notificationTarget } from './api';
import { useNotifications } from './NotificationsProvider';
import { observeNotifications } from './seen';
import './notifications.scss';

const { Header, Content, Footer } = Layout;
const icons = { likes: HeartIcon, reposts: RepostIcon, subscriptions: FollowIcon, author_comments: BellOnIcon };
const HISTORY_LIMIT = 16;
export default function NotificationsPage() {
  const header = useRef(null), footer = useRef(null), content = useRef(null);
  const pending = useRef(false), abort = useRef(null), mounted = useRef(false);
  const seenIds = useRef(new Set()), seenTimer = useRef(null), seenRequest = useRef(null);
  const scrollAnchor = useRef(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true), [error, setError] = useState(false);
  const navigate = useNavigate(), { t } = useTranslation(), { refresh } = useNotifications();
  const rememberScroll = useCallback(() => {
    const root = content.current;
    if (!root || root.scrollTop <= 0 || scrollAnchor.current) return;
    const top = root.getBoundingClientRect().top;
    // Keep a few visible successors too, in case the first visible card is removed.
    scrollAnchor.current = { cards: [...root.querySelectorAll('[data-notification-id]')]
      .filter(node => node.getBoundingClientRect().bottom > top).slice(0, 4)
      .map(node => ({ id: node.dataset.notificationId, offset: node.getBoundingClientRect().top - top })) };
  }, []);
  const userId = getUser()?.id, token = getLocalStorage(config.token);
  const session = useRef(null); session.current = { userId, token };
  const { contentHeight, contentTop } = usePageLayout(header, footer);
  const load = useCallback(async (background = false) => {
    if (!userId || !token || pending.current) return;
    pending.current = true;
    if (!background) { setLoading(true); setError(false); }
    const controller = new AbortController(); abort.current = controller;
    try {
      const result = await notificationRequest('', { signal: controller.signal, token });
      if (!mounted.current || controller.signal.aborted) return;
      if (!Array.isArray(result.list)) throw new Error('Invalid notification history');
      rememberScroll();
      // One bounded snapshot reconciles new events, remote reads and unavailable
      // content. A late read response cannot undo a successful local seen write.
      setList(old => result.list.slice(0, HISTORY_LIMIT).map(row => ({ ...row,
        seen: row.seen || old.some(item => item.id === row.id && item.seen) })));
      setError(false);
    } catch { if (mounted.current && !controller.signal.aborted && !background) setError(true); }
    finally { if (mounted.current && !controller.signal.aborted) { pending.current = false; setLoading(false); } }
  }, [userId, token, rememberScroll]);

  useEffect(() => {
    mounted.current = true; pending.current = false; seenIds.current.clear();
    scrollAnchor.current = null;
    setList([]);
    if (!userId || !token) navigate(`${url('/login')}?from=${encodeURIComponent(url('/notifications'))}`, { replace: true });
    else void load();
    return () => { mounted.current = false; abort.current?.abort(); seenRequest.current?.abort();
      seenRequest.current = null; clearTimeout(seenTimer.current); seenTimer.current = null; };
  }, [userId, token, load, navigate]);

  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'hidden') void load(true);
    };
    window.addEventListener('wiw:notifications-refresh', sync);
    return () => window.removeEventListener('wiw:notifications-refresh', sync);
  }, [load]);

  useLayoutEffect(() => {
    const root = content.current, anchor = scrollAnchor.current;
    if (root && anchor) {
      root.style.removeProperty('--notification-scroll-space');
      const saved = anchor.cards.find(card => root.querySelector(`[data-notification-id="${card.id}"]`));
      if (saved) {
        const node = root.querySelector(`[data-notification-id="${saved.id}"]`);
        const nextTop = root.scrollTop + node.getBoundingClientRect().top - root.getBoundingClientRect().top - saved.offset;
        // Replacing the oldest card shortens the scroll range at its lower end.
        // Keep enough trailing space to avoid moving the card being read.
        const extra = Math.max(0, nextTop - (root.scrollHeight - root.clientHeight));
        if (extra) root.style.setProperty('--notification-scroll-space', `${extra}px`);
        root.scrollTop = nextTop;
      } else root.scrollTop = 0;
    }
    scrollAnchor.current = null;
  }, [list]);

  const flushSeen = useCallback(async function flush() {
    seenTimer.current = null;
    if (seenRequest.current || !seenIds.current.size || !mounted.current || !token
      || session.current.userId !== userId || session.current.token !== token) return;
    const controller = new AbortController(); seenRequest.current = controller;
    const ids = [...seenIds.current].slice(0, 100), checked = new Set(ids);
    ids.forEach(id => seenIds.current.delete(id));
    const current = () => mounted.current && !controller.signal.aborted
      && session.current.userId === userId && session.current.token === token;
    let failed = false;
    try {
      await notificationRequest('/seen', { method: 'POST', body: { ids }, token, signal: controller.signal });
      if (current()) {
        setList(old => old.map(row => checked.has(row.id) && !row.seen ? { ...row, seen: true } : row));
        void refresh(true);
      }
    } catch {
      if (current()) { failed = true; ids.forEach(id => seenIds.current.add(id)); }
    } finally {
      if (seenRequest.current === controller) seenRequest.current = null;
      if (current() && seenIds.current.size) seenTimer.current = setTimeout(flush, failed ? 10000 : 120);
    }
  }, [userId, token, refresh]);

  useEffect(() => observeNotifications(content.current, id => {
    seenIds.current.add(id);
    if (!seenTimer.current && !seenRequest.current) seenTimer.current = setTimeout(flushSeen, 120);
  }), [list, flushSeen]);

  return <Layout className="app-layout user-page notifications-page">
    <Header ref={header} className="header"><WiwHeaderTitle title={t('notifications:title')} /></Header>
    <Content ref={content} className="content-layot wrapper-padding notifications-content" style={{ top: contentTop, height: contentHeight }}
      onScroll={event => { if (event.currentTarget.scrollTop === 0) event.currentTarget.style.removeProperty('--notification-scroll-space'); }}>
      {error && <LoadFailure onRetry={() => load()} />}
      <div className="notifications-list">
        {list.map(item => {
          const Drawing = icons[item.kind] || BellOnIcon, target = notificationTarget(item.target);
          const label = t(`notifications:${item.kind}`, { name: createName(item.actor) || `@${item.actor.user_name}` });
          return <article key={item.id} className="notification-card" data-notification-id={item.id} data-seen={item.seen}>
            <Link className="notification-avatar" to={url(`/user/${encodeURIComponent(item.actor.user_name)}`)} aria-label={`@${item.actor.user_name}`}>
              <UserImage picture={item.actor.picture} />
            </Link>
            <Link className="notification-target" to={url(target || '/notifications')}>
              <Drawing /><span>{label}</span>
            </Link>
            {!item.seen && <PlusIcon className="notification-new" aria-label={t('notifications:newActivity')} />}
          </article>;
        })}
      </div>
      {!loading && !error && !list.length && <p className="notifications-empty">{t('notifications:empty')}</p>}
      {loading && <BrandLoader />}
    </Content>
    <Footer ref={footer}><ButtonPanelReaction /></Footer>
  </Layout>;
}
